import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useSnackbar } from 'notistack';
import { ProductContext } from '../../ProductProvider';
import { Button, Modal, Table, Dropdown, ButtonGroup, Spinner, Form, Row, Col, Badge } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import ArticlesForm from './ArticlesForm';
import './ListArticles.css';

const config = require('../../config.json');

export default function ListArticles(props) {
  const { journalData, loggedUser, articleData, setArticleData } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();
  const { viewOnly = false, formType, handleTabSelect } = props;
  const [oldArticle, setOldArticle] = useState({});
  // Data & Loading States
  const [loaded, setLoaded] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  // Filter & Search Controls
  const [search, setSearch] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [volumeFilter, setVolumeFilter] = useState('');
  const [sortBy, setSortBy] = useState('seq_asc');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Modal State
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [formTypeArticle, setFormTypeArticle] = useState('New');
  const [modalTitle, setModalTitle] = useState('Add Journal Article');

  const handleArticleClose = () => setShowArticleModal(false);
  const handleArticleShow = () => setShowArticleModal(true);

  // Delete State & Handlers
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (e, item) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setArticleToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setArticleToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!articleToDelete) return;
    setIsDeleting(true);
    try {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = loggedUser?.website || articleToDelete?.clientID || 'VJCollege';
      const volissuesNoarticleID = articleToDelete.volissuesNoarticleID || articleToDelete.articleID;

      const payload = {
        clientID: clientID,
        volissuesNoarticleID: volissuesNoarticleID
      };

      console.log('Attempting to delete article with payload:', payload);

      /*await axios.delete(
        `${config.api.invokeURL}/vjlibrary/journal/catalogue/articles`,
        {
          data: {
            clientID: clientID,
            volissuesNoarticleID: volissuesNoarticleID
          },
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        }
      );*/

      await axios.post(`${config.api.invokeURL}/vjlibrary/journal/catalogue/articles/delete`, payload, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      enqueueSnackbar(`Article "${articleToDelete.articleTitle || volissuesNoarticleID}" deleted successfully!`, {
        variant: 'success'
      });
      setShowDeleteModal(false);
      setArticleToDelete(null);
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      console.error('Error details:', error.response?.data || error.response || error);
      const serverMessage = error.response?.data?.message || error.response?.data || error.message;
      enqueueSnackbar(`Error deleting article: ${serverMessage}`, {
        variant: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Active Issue & Journal identifiers
  const activeJournalID = articleData?.journalID || journalData?.journalID || '';
  const activeVolissuesNo = articleData?.volissuesNo || journalData?.volissuesNo || '';

  // Fetch articles from API
  const fetchArticles = useCallback(async () => {
    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = loggedUser?.website || 'VJCollege';

      // Determine search criteria: prioritize volissuesNo if available, otherwise search by journalID
      let searchTopic = 'VolissuesNo';
      let searchValue = activeVolissuesNo;

      if (!searchValue && activeJournalID) {
        searchTopic = 'JournalID';
        searchValue = activeJournalID;
      }

      if (!searchValue) {
        setSearchResult([]);
        setLoaded(true);
        return;
      }

      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/journal/articles`, {
        params: {
          fetchKey: searchValue,
          fetchOption: searchTopic,
          clientID: clientID
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const data = Array.isArray(response.data) ? response.data : [];
      setSearchResult(data);
      setLoaded(true);
    } catch (error) {
      console.error('Error fetching articles:', error);
      enqueueSnackbar(`Error fetching articles: ${error.message}`, { variant: 'warning' });
      setSearchResult([]);
      setLoaded(true);
    }
  }, [activeJournalID, activeVolissuesNo, enqueueSnackbar, loggedUser]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Open Add / Edit / View Modals
  const handleOpenModal = (event, type, item = null) => {
    if (event) event.preventDefault();
    setOldArticle({});
    // console.log("Item :", item);
    if (item) {
      setArticleData({
        ...item,
        journalID: item.journalID || activeJournalID,
        volissuesNo: item.volissuesNo || activeVolissuesNo
      });
      setOldArticle({
        ...item,
        journalID: item.journalID || activeJournalID,
        volissuesNo: item.volissuesNo || activeVolissuesNo
      });
    } else if (!activeVolissuesNo) {
      // Clear data for New article but preserve Journal & Volume context
      handleTabSelect('Issues');
      return;
    }
    else {
      setArticleData({
        journalID: activeJournalID,
        volissuesNo: activeVolissuesNo,
        volissuesNoarticleID: '',
        articleID: '',
        articleTitle: '',
        articleSubTitle: '',
        articleAuthor: '',
        articleKeywords: '',
        articleDescription: '',
        startPageNo: '',
        endPageNo: ''
      });
    }

    if (type === 'View') {
      setModalTitle('View Journal Article');
      setFormTypeArticle('View');
    } else if (type === 'Edit') {
      setModalTitle('Edit Journal Article');
      setFormTypeArticle('Edit');
    } else {
      setModalTitle('Add Journal Article');
      setFormTypeArticle('New');
    }
    handleArticleShow();
  };

  // Quick Copy Citation helper
  const handleCopyCitation = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    const citation = `${item.articleAuthor || 'Unknown Author'}. "${item.articleTitle || 'Untitled'}." ${journalData?.journalTitle || 'Journal'} ${item.volissuesNo || ''} (pp. ${item.startPageNo || '?'}-${item.endPageNo || '?'}).`;
    navigator.clipboard.writeText(citation).then(() => {
      setCopiedId(item.articleID || item.volissuesNoarticleID);
      enqueueSnackbar('Citation copied to clipboard!', { variant: 'info', autoHideDuration: 2500 });
      setTimeout(() => setCopiedId(null), 3000);
    });
  };

  // Helper to extract numeric suffix from articleID for sorting
  const extractSeqNum = useCallback((idStr) => {
    if (!idStr) return 0;
    const match = String(idStr).match(/[-_](\d+)$/);
    if (match) return parseInt(match[1], 10);
    const pure = parseInt(idStr, 10);
    return isNaN(pure) ? 0 : pure;
  }, []);

  // Filter and Sort Pipeline
  const filteredArticles = useMemo(() => {
    let result = [...searchResult];

    // 1. Text Search across Title, Subtitle, Author, Keywords, Description, ID
    if (search.trim() !== '') {
      const q = search.toLowerCase().trim();
      result = result.filter((a) =>
        (a.articleTitle || '').toLowerCase().includes(q) ||
        (a.articleSubTitle || '').toLowerCase().includes(q) ||
        (a.articleAuthor || '').toLowerCase().includes(q) ||
        (a.articleKeywords || '').toLowerCase().includes(q) ||
        (a.articleDescription || '').toLowerCase().includes(q) ||
        (a.articleID || '').toLowerCase().includes(q)
      );
    }

    // 2. Filter by Author
    if (authorFilter !== '') {
      result = result.filter((a) => (a.articleAuthor || '').toUpperCase() === authorFilter.toUpperCase());
    }

    // 3. Filter by Volume / Issue
    if (volumeFilter !== '') {
      result = result.filter((a) => (a.volissuesNo || '').toUpperCase() === volumeFilter.toUpperCase());
    }

    // 4. Sorting Logic
    result.sort((a, b) => {
      switch (sortBy) {
        case 'seq_asc':
          return extractSeqNum(a.articleID) - extractSeqNum(b.articleID);
        case 'seq_desc':
          return extractSeqNum(b.articleID) - extractSeqNum(a.articleID);
        case 'page_asc':
          return (parseInt(a.startPageNo, 10) || 0) - (parseInt(b.startPageNo, 10) || 0);
        case 'page_desc':
          return (parseInt(b.startPageNo, 10) || 0) - (parseInt(a.startPageNo, 10) || 0);
        case 'title_asc':
          return (a.articleTitle || '').localeCompare(b.articleTitle || '');
        case 'title_desc':
          return (b.articleTitle || '').localeCompare(a.articleTitle || '');
        case 'author_asc':
          return (a.articleAuthor || '').localeCompare(b.articleAuthor || '');
        case 'author_desc':
          return (b.articleAuthor || '').localeCompare(a.articleAuthor || '');
        case 'newest':
          return new Date(b.updatedOn || 0) - new Date(a.updatedOn || 0);
        case 'oldest':
          return new Date(a.updatedOn || 0) - new Date(b.updatedOn || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [searchResult, search, authorFilter, volumeFilter, sortBy, extractSeqNum]);

  // Unique Lists for Dropdowns
  const uniqueAuthors = useMemo(() => {
    const authors = searchResult
      .map((a) => (a.articleAuthor || '').trim())
      .filter((a) => a.length > 0);
    return Array.from(new Set(authors)).sort();
  }, [searchResult]);

  const uniqueVolumes = useMemo(() => {
    const vols = searchResult
      .map((a) => (a.volissuesNo || '').trim())
      .filter((v) => v.length > 0);
    return Array.from(new Set(vols)).sort();
  }, [searchResult]);

  // Summary KPI Metrics
  const kpiStats = useMemo(() => {
    const total = searchResult.length;
    const authorsCount = uniqueAuthors.length;

    // Calculate overall page coverage
    const pages = searchResult
      .map((a) => ({
        start: parseInt(a.startPageNo, 10),
        end: parseInt(a.endPageNo, 10)
      }))
      .filter((p) => !isNaN(p.start) && !isNaN(p.end));

    let pageSpan = '—';
    if (pages.length > 0) {
      const minPage = Math.min(...pages.map((p) => p.start));
      const maxPage = Math.max(...pages.map((p) => p.end));
      pageSpan = `pp. ${minPage}–${maxPage}`;
    }

    return { total, authorsCount, pageSpan };
  }, [searchResult, uniqueAuthors]);

  // Pagination Logic
  const totalPages = Math.max(1, Math.ceil(filteredArticles.length / itemsPerPage));
  const paginatedArticles = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    return filteredArticles.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredArticles, currentPage, itemsPerPage]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, authorFilter, volumeFilter, itemsPerPage]);

  // CSV Export Data
  const csvHeaders = [
    { label: 'Article ID', key: 'articleID' },
    { label: 'Journal ID', key: 'journalID' },
    { label: 'Volume / Issue', key: 'volissuesNo' },
    { label: 'Article Title', key: 'articleTitle' },
    { label: 'Subtitle', key: 'articleSubTitle' },
    { label: 'Author(s)', key: 'articleAuthor' },
    { label: 'Start Page', key: 'startPageNo' },
    { label: 'End Page', key: 'endPageNo' },
    { label: 'Keywords', key: 'articleKeywords' },
    { label: 'Abstract', key: 'articleDescription' },
    { label: 'Updated On', key: 'updatedOn' }
  ];

  return (
    <div className="list-articles-container">
      {/* Modal for Add / Edit / View Article */}
      <Modal show={showArticleModal} onHide={handleArticleClose} size="lg" centered backdrop="static">
        <Modal.Header closeButton className="bg-light border-0 pb-0">
          <Modal.Title className="fw-bold fs-5 text-dark">
            <i className="fa fa-file-alt text-primary me-2"></i>
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-2">
          <ArticlesForm
            handleClose={handleArticleClose}
            oldArticle={oldArticle}
            viewOnly={viewOnly}
            formType={formTypeArticle}
            existingArticles={searchResult}
            onSuccess={() => {
              fetchArticles();
            }}
          />
        </Modal.Body>
      </Modal>

      {/* Modal for Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered backdrop="static">
        <Modal.Header closeButton className="bg-light border-0 pb-0">
          <Modal.Title className="fw-bold fs-5 text-danger d-flex align-items-center gap-2">
            <i className="fa fa-exclamation-triangle"></i>
            Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-3">
          <p className="text-secondary mb-3">
            Are you sure you want to delete this article? This action cannot be undone.
          </p>
          {articleToDelete && (
            <div className="p-3 bg-light rounded-3 border">
              <div className="fw-bold text-dark mb-1">{articleToDelete.articleTitle || 'Untitled'}</div>
              {articleToDelete.articleSubTitle && (
                <div className="text-muted small fst-italic mb-2">{articleToDelete.articleSubTitle}</div>
              )}
              <div className="small text-secondary d-flex flex-column gap-1">
                <div>
                  <strong>Article ID:</strong>{' '}
                  <span className="font-monospace text-primary">{articleToDelete.articleID || articleToDelete.volissuesNoarticleID}</span>
                </div>
                {articleToDelete.articleAuthor && (
                  <div>
                    <strong>Author:</strong> {articleToDelete.articleAuthor}
                  </div>
                )}
                {articleToDelete.volissuesNo && (
                  <div>
                    <strong>Volume / Issue:</strong> {articleToDelete.volissuesNo}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 px-4 pb-4">
          <Button variant="outline-secondary" onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            className="d-flex align-items-center gap-2 shadow-sm"
          >
            {isDeleting ? (
              <>
                <Spinner animation="border" size="sm" />
                Deleting...
              </>
            ) : (
              <>
                <i className="fa fa-trash"></i>
                Delete Article
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 1. Header & Context Summary Banner */}
      <div className="articles-header-banner">
        <div>
          <div className="articles-header-title">
            <i className="fa fa-layer-group"></i>
            Journal Articles Index
          </div>
          <div className="articles-header-subtitle">
            {journalData?.journalTitle ? journalData.journalTitle : 'Vidyajyoti College Theological Repository'}
          </div>
        </div>

        <div className="articles-header-actions">
          {filteredArticles.length > 0 && (
            <CSVLink
              data={filteredArticles}
              headers={csvHeaders}
              filename={`articles_${activeVolissuesNo || activeJournalID || 'index'}_${new Date().toISOString().slice(0, 10)}.csv`}
              className="btn-export-csv"
            >
              <i className="fa fa-file-csv text-success"></i> Export CSV
            </CSVLink>
          )}

          {formType !== 'View' && !viewOnly && (
            <Button
              variant="light"
              className="fw-bold shadow-sm d-flex align-items-center gap-2 px-3 py-2 text-primary"
              onClick={(e) => handleOpenModal(e, 'New')}
            >
              <i className="fa fa-plus-circle text-primary"></i> Add Article
            </Button>
          )}
        </div>
      </div>

      {/* 2. KPI Summary Cards */}
      <div className="articles-kpi-grid">
        <div className="article-kpi-card kpi-total">
          <div className="kpi-label">
            <i className="fa fa-file-alt text-primary"></i> Total Articles
          </div>
          <div className="kpi-value">{kpiStats.total}</div>
          <div className="kpi-subtext">Registered in this catalog</div>
        </div>

        <div className="article-kpi-card kpi-authors">
          <div className="kpi-label">
            <i className="fa fa-user-friends text-success"></i> Authors
          </div>
          <div className="kpi-value">{kpiStats.authorsCount}</div>
          <div className="kpi-subtext">Contributing scholars</div>
        </div>

        <div className="article-kpi-card kpi-pages">
          <div className="kpi-label">
            <i className="fa fa-book-open text-warning"></i> Page Span
          </div>
          <div className="kpi-value" style={{ fontSize: '1.25rem', paddingTop: '4px' }}>
            {kpiStats.pageSpan}
          </div>
          <div className="kpi-subtext">Issue page range</div>
        </div>

        <div className="article-kpi-card kpi-issue">
          <div className="kpi-label">
            <i className="fa fa-folder text-purple"></i> Active Issue
          </div>
          <div className="kpi-value" style={{ fontSize: '1.25rem', paddingTop: '4px' }}>
            {activeVolissuesNo || 'All Issues'}
          </div>
          <div className="kpi-subtext">Journal: {activeJournalID || 'N/A'}</div>
        </div>
      </div>

      {/* 3. Search, Filter & Controls Toolbar */}
      <div className="articles-toolbar-card">
        <Row className="g-3 align-items-center">
          {/* Live Search Input */}
          <Col lg={4} md={6}>
            <div className="search-input-wrapper">
              <i className="fa fa-search search-input-icon"></i>
              <Form.Control
                type="text"
                placeholder="Search by title, author, keyword, or ID..."
                className="articles-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="btn btn-sm text-muted position-absolute end-0 top-50 translate-middle-y me-2 border-0 bg-transparent"
                  onClick={() => setSearch('')}
                >
                  <i className="fa fa-times-circle"></i>
                </button>
              )}
            </div>
          </Col>

          {/* Filter by Author */}
          <Col lg={2} sm={6}>
            <Form.Select
              className="filter-select"
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
            >
              <option value="">All Authors ({uniqueAuthors.length})</option>
              {uniqueAuthors.map((author) => (
                <option key={author} value={author}>
                  {author}
                </option>
              ))}
            </Form.Select>
          </Col>

          {/* Filter by Issue / Volume */}
          {uniqueVolumes.length > 1 && (
            <Col lg={2} sm={6}>
              <Form.Select
                className="filter-select"
                value={volumeFilter}
                onChange={(e) => setVolumeFilter(e.target.value)}
              >
                <option value="">All Issues ({uniqueVolumes.length})</option>
                {uniqueVolumes.map((vol) => (
                  <option key={vol} value={vol}>
                    {vol}
                  </option>
                ))}
              </Form.Select>
            </Col>
          )}

          {/* Sort By Dropdown */}
          <Col lg={uniqueVolumes.length > 1 ? 2 : 4} sm={6}>
            <Form.Select
              className="filter-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="seq_asc">Sort: Article ID (1 &rarr; N)</option>
              <option value="seq_desc">Sort: Article ID (N &rarr; 1)</option>
              <option value="page_asc">Sort: Start Page (Low &rarr; High)</option>
              <option value="page_desc">Sort: Start Page (High &rarr; Low)</option>
              <option value="title_asc">Sort: Title (A &rarr; Z)</option>
              <option value="title_desc">Sort: Title (Z &rarr; A)</option>
              <option value="author_asc">Sort: Author (A &rarr; Z)</option>
              <option value="newest">Sort: Recently Updated</option>
            </Form.Select>
          </Col>

          {/* View Toggle (Table / Cards) */}
          <Col lg={2} sm={6} className="d-flex justify-content-end">
            <div className="view-toggle-group">
              <button
                type="button"
                className={`btn-view-toggle ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Table View"
              >
                <i className="fa fa-list"></i> Table
              </button>
              <button
                type="button"
                className={`btn-view-toggle ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
                title="Cards View"
              >
                <i className="fa fa-th-large"></i> Cards
              </button>
            </div>
          </Col>
        </Row>
      </div>

      {/* 4. Main Content Area */}
      {!loaded ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <div className="mt-3 text-muted fw-semibold">Loading articles catalogue...</div>
        </div>
      ) : searchResult.length === 0 ? (
        /* Empty State (No articles in this issue) */
        <div className="articles-empty-card">
          <div className="empty-state-icon">
            <i className="fa fa-book-reader"></i>
          </div>
          <div className="empty-state-title">No Articles Registered Yet</div>
          <div className="empty-state-desc">
            No articles have been indexed for issue &quot;{activeVolissuesNo || 'this journal'}&quot;. Click below to add the first article with auto-generated sequential numbering.
          </div>
          {formType !== 'View' && !viewOnly && (
            <Button
              variant="primary"
              size="lg"
              className="px-4 py-2 fw-semibold shadow-sm"
              onClick={(e) => handleOpenModal(e, 'New')}
            >
              <i className="fa fa-plus-circle me-2"></i> Add First Article
            </Button>
          )}
        </div>
      ) : filteredArticles.length === 0 ? (
        /* No Match State (Filters returned 0 results) */
        <div className="articles-empty-card">
          <div className="empty-state-icon text-secondary" style={{ background: '#f1f5f9' }}>
            <i className="fa fa-filter"></i>
          </div>
          <div className="empty-state-title">No Articles Found</div>
          <div className="empty-state-desc">
            No articles matched your current search &quot;{search}&quot; or filter criteria.
          </div>
          <Button
            variant="outline-primary"
            onClick={() => {
              setSearch('');
              setAuthorFilter('');
              setVolumeFilter('');
            }}
          >
            <i className="fa fa-redo me-1"></i> Clear Filters
          </Button>
        </div>
      ) : viewMode === 'table' ? (
        /* ================= TABLE VIEW ================= */
        <div className="articles-table-wrapper">
          <Table hover responsive className="articles-table">
            <thead>
              <tr>
                <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                <th style={{ width: '130px' }}>Article ID</th>
                <th>Article Title & Subtitle</th>
                <th>Author(s)</th>
                <th>Pages</th>
                <th>Keywords</th>
                <th style={{ width: '90px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedArticles.map((item, index) => {
                const globalIndex = (currentPage - 1) * itemsPerPage + index + 1;
                const keywords = (item.articleKeywords || '')
                  .split(',')
                  .map((k) => k.trim())
                  .filter((k) => k.length > 0);

                return (
                  <tr key={item.articleID || item.volissuesNoarticleID || index}>
                    <td style={{ textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>
                      {globalIndex}
                    </td>

                    <td>
                      <span className="article-id-pill" title={item.articleID}>
                        <i className="fa fa-fingerprint me-1"></i>
                        {item.articleID || item.volissuesNoarticleID}
                      </span>
                    </td>

                    <td className="article-title-cell">
                      <div className="article-main-title">{item.articleTitle}</div>
                      {item.articleSubTitle && (
                        <div className="article-sub-title">{item.articleSubTitle}</div>
                      )}
                    </td>

                    <td>
                      <span className="author-chip">
                        <i className="fa fa-user-edit text-primary"></i>
                        {item.articleAuthor || '—'}
                      </span>
                    </td>

                    <td>
                      {item.startPageNo && item.endPageNo ? (
                        <span className="page-range-pill" title={`${parseInt(item.endPageNo, 10) - parseInt(item.startPageNo, 10) + 1} pages total`}>
                          <i className="fa fa-file-alt"></i> pp. {item.startPageNo}–{item.endPageNo}
                        </span>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>

                    <td>
                      {keywords.length > 0 ? (
                        <div className="table-keyword-tags">
                          {keywords.slice(0, 3).map((kw, ki) => (
                            <span key={ki} className="table-tag">
                              #{kw}
                            </span>
                          ))}
                          {keywords.length > 3 && (
                            <span className="table-tag text-primary">+{keywords.length - 3}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>

                    <td style={{ textAlign: 'center' }}>
                      <Dropdown as={ButtonGroup} align="end">
                        <Dropdown.Toggle
                          as={Button}
                          variant="link"
                          className="p-0 border-0 text-muted shadow-none"
                          style={{ width: '32px', height: '32px' }}
                        >
                          <i className="fa fa-ellipsis-v text-primary" style={{ fontSize: '1.1rem' }}></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="shadow-lg border-0 rounded-3 p-2">
                          <Dropdown.Item
                            onClick={(e) => handleOpenModal(e, 'View', item)}
                            className="d-flex align-items-center gap-2 py-2 rounded-2"
                          >
                            <i className="fa fa-eye text-primary"></i> View Details
                          </Dropdown.Item>

                          {formType !== 'View' && !viewOnly && (
                            <Dropdown.Item
                              onClick={(e) => handleOpenModal(e, 'Edit', item)}
                              className="d-flex align-items-center gap-2 py-2 rounded-2"
                            >
                              <i className="fa fa-pen text-warning"></i> Edit Article
                            </Dropdown.Item>
                          )}

                          <Dropdown.Item
                            onClick={(e) => handleCopyCitation(e, item)}
                            className="d-flex align-items-center gap-2 py-2 rounded-2"
                          >
                            <i className={copiedId === (item.articleID || item.volissuesNoarticleID) ? 'fa fa-check text-success' : 'fa fa-quote-right text-info'}></i>
                            {copiedId === (item.articleID || item.volissuesNoarticleID) ? 'Copied!' : 'Copy Citation'}
                          </Dropdown.Item>

                          {formType !== 'View' && !viewOnly && (
                            <>
                              <Dropdown.Divider />
                              <Dropdown.Item
                                onClick={(e) => handleDeleteClick(e, item)}
                                className="d-flex align-items-center gap-2 py-2 rounded-2 text-danger"
                              >
                                <i className="fa fa-trash text-danger"></i> Delete Article
                              </Dropdown.Item>
                            </>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ) : (
        /* ================= CARDS VIEW ================= */
        <div className="articles-card-grid">
          {paginatedArticles.map((item, index) => {
            const keywords = (item.articleKeywords || '')
              .split(',')
              .map((k) => k.trim())
              .filter((k) => k.length > 0);

            return (
              <div key={item.articleID || item.volissuesNoarticleID || index} className="article-card-item">
                <div>
                  <div className="article-card-header">
                    <span className="article-id-pill">
                      <i className="fa fa-fingerprint me-1"></i>
                      {item.articleID || item.volissuesNoarticleID}
                    </span>

                    {item.startPageNo && item.endPageNo && (
                      <span className="page-range-pill">
                        pp. {item.startPageNo}–{item.endPageNo}
                      </span>
                    )}
                  </div>

                  <h5 className="article-card-title">{item.articleTitle}</h5>
                  {item.articleSubTitle && (
                    <div className="text-secondary small fst-italic mb-2">{item.articleSubTitle}</div>
                  )}

                  <div className="article-card-author">
                    <i className="fa fa-user-edit"></i>
                    {item.articleAuthor || 'Unknown Author'}
                  </div>

                  {item.articleDescription && (
                    <div className="article-card-abstract">
                      {item.articleDescription.length > 140
                        ? `${item.articleDescription.slice(0, 140)}...`
                        : item.articleDescription}
                    </div>
                  )}

                  {keywords.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mb-3">
                      {keywords.slice(0, 4).map((kw, ki) => (
                        <Badge key={ki} bg="light" text="dark" className="border fw-normal">
                          #{kw}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="article-card-meta">
                  <span className="text-muted small">
                    <i className="fa fa-folder me-1"></i>
                    {item.volissuesNo || activeVolissuesNo}
                  </span>

                  <div className="article-card-actions">
                    <Button
                      variant="outline-info"
                      size="sm"
                      className="btn-card-action"
                      onClick={(e) => handleCopyCitation(e, item)}
                      title="Copy Citation"
                    >
                      <i className={copiedId === (item.articleID || item.volissuesNoarticleID) ? 'fa fa-check' : 'fa fa-quote-right'}></i>
                    </Button>

                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="btn-card-action"
                      onClick={(e) => handleOpenModal(e, 'View', item)}
                    >
                      <i className="fa fa-eye"></i> View
                    </Button>

                    {formType !== 'View' && !viewOnly && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          className="btn-card-action"
                          onClick={(e) => handleOpenModal(e, 'Edit', item)}
                        >
                          <i className="fa fa-pen"></i> Edit
                        </Button>

                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="btn-card-action"
                          onClick={(e) => handleDeleteClick(e, item)}
                          title="Delete Article"
                        >
                          <i className="fa fa-trash"></i>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 5. Pagination Controls */}
      {filteredArticles.length > 0 && (
        <div className="articles-pagination-bar">
          <div className="pagination-info">
            Showing <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> to{' '}
            <strong>{Math.min(currentPage * itemsPerPage, filteredArticles.length)}</strong> of{' '}
            <strong>{filteredArticles.length}</strong> articles
            {filteredArticles.length !== searchResult.length && (
              <span className="text-muted ms-1">(filtered from {searchResult.length} total)</span>
            )}
          </div>

          <div className="d-flex align-items-center gap-3">
            {/* Items Per Page Selector */}
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Show:</span>
              <Form.Select
                size="sm"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(parseInt(e.target.value, 10))}
                style={{ width: '75px', borderRadius: '8px' }}
              >
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={50}>50</option>
              </Form.Select>
            </div>

            {/* Page Buttons */}
            {totalPages > 1 && (
              <div className="pagination-nav">
                <button
                  type="button"
                  className="btn-page-nav"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  title="Previous Page"
                >
                  <i className="fa fa-chevron-left"></i>
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Only show a window of pages if totalPages > 7
                  if (
                    totalPages > 7 &&
                    page !== 1 &&
                    page !== totalPages &&
                    Math.abs(page - currentPage) > 2
                  ) {
                    if (Math.abs(page - currentPage) === 3) {
                      return (
                        <span key={page} className="px-1 text-muted">
                          ...
                        </span>
                      );
                    }
                    return null;
                  }

                  return (
                    <button
                      key={page}
                      type="button"
                      className={`btn-page-nav ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  type="button"
                  className="btn-page-nav"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  title="Next Page"
                >
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
